import React from 'react';
import useSeoTags from '../utils/useSeoTags';

/**
 * Privacy Policy for the Funeral Professional (B2B) portal at
 * `/funeral-directors/privacy`. Separate from the consumer
 * `/privacy` page to reflect the very different data collected on the
 * B2B side (funeral firm account data + decedent details input by
 * staff on behalf of families).
 */
const FdPrivacy = () => {
  const updated = 'April 20, 2026';
  useSeoTags({
    title: 'Funeral Professional Portal — Privacy Policy | ObitAuthor',
    description:
      'Privacy Policy for the ObitAuthor Funeral Professional portal: how we handle firm account data, decedent details, and obituary content on behalf of funeral homes.',
    canonical: 'https://obitauthor.com/funeral-directors/privacy',
    robots: 'index, follow',
  });
  return (
    <div className="container py-5 fd-auth fd-legal">
      <div className="row justify-content-center">
        <div className="col-lg-10 col-xl-9">
          <div className="glass-card fd-legal-card">
            <span className="fd-auth-badge">For funeral professionals</span>
            <h1 className="fd-legal-title mt-3 mb-2">
              Funeral Professional Portal — Privacy Policy
            </h1>
            <p className="fd-legal-updated">Last updated {updated}</p>

            <p>
              This Privacy Policy describes how ObitAuthor handles
              information collected through the Funeral Professional
              portal at <em>obitauthor.com/funeral-directors</em>. It
              applies to the funeral home, cremation service, or other
              professional firm that creates the account
              (&ldquo;Firm&rdquo;) and to the staff members the Firm
              authorizes to use the portal. If you are a member of a
              family using the consumer ObitAuthor site, our consumer{' '}
              <a href="/privacy">Privacy Policy</a> applies to you.
            </p>

            <h2>1. Information we collect</h2>
            <p>
              We collect only what we need to operate the portal on the
              Firm&rsquo;s behalf:
            </p>
            <ul>
              <li>
                <strong>Account information.</strong> Firm name, account
                owner&rsquo;s name and email, hashed password, uploaded
                logo, and billing identifiers returned by our payment
                processor. We never see or store raw card numbers.
              </li>
              <li>
                <strong>Decedent details.</strong> The biographical
                details your staff (or a family working with your
                staff) enters to draft an obituary: name, dates, life
                events, relationships, and any freeform notes.
              </li>
              <li>
                <strong>Draft and saved obituaries.</strong> The text
                and rich-HTML versions your staff reviews, edits, and
                saves, along with the branded PDF file we render.
              </li>
              <li>
                <strong>Usage information.</strong> Basic request
                metadata (timestamps, paths, aggregated error counts)
                used to operate and troubleshoot the service. We do
                not use third-party advertising or analytics trackers
                on the portal.
              </li>
            </ul>

            <h2>2. How we use information</h2>
            <ul>
              <li>
                To authenticate your Firm&rsquo;s staff and show them
                the obituaries and credits tied to your account.
              </li>
              <li>
                To draft, regenerate, edit, and deliver obituaries and
                branded PDFs from the details your staff provides.
              </li>
              <li>
                To bill for paid credit bundles and maintain a record
                of completed purchases.
              </li>
              <li>
                To send operational messages (password resets,
                material Terms changes, incident notices).
              </li>
              <li>
                To protect the portal and investigate abuse, fraud, or
                violations of the Terms.
              </li>
            </ul>

            <h2>3. AI processing</h2>
            <p>
              Drafts are generated by large language models. The text
              your staff submits is sent to those models only to
              produce the requested obituary. We do not use Firm-side
              inputs or drafts to train third-party models, and we do
              not sell obituary content to anyone.
            </p>

            <h2>4. Sharing</h2>
            <p>
              We share information only with the vendors we need to
              run the portal:
            </p>
            <ul>
              <li>
                Our payment processor, to charge for credit bundles and
                maintain PCI-compliant tokenized billing records.
              </li>
              <li>
                Our cloud hosting and email-delivery providers, which
                process data solely to operate the service on our
                behalf.
              </li>
              <li>
                Model providers that generate obituary drafts from the
                inputs your staff supplies.
              </li>
              <li>
                Legal or safety disclosures when required by law, to
                enforce the Terms, or to protect the rights and
                safety of users and the public.
              </li>
            </ul>
            <p>
              We do not sell personal information, and we do not share
              Firm or decedent content with advertisers.
            </p>

            <h2>5. Your Firm&rsquo;s role as controller</h2>
            <p>
              Your Firm decides which decedent and family information
              to enter into the portal and is the controller of that
              information under applicable privacy laws. ObitAuthor
              processes that information on the Firm&rsquo;s
              instructions to produce the obituaries your Firm
              delivers to families.
            </p>

            <h2>6. Retention</h2>
            <p>
              We retain Firm account data for as long as the account
              is active and for a reasonable period thereafter to
              satisfy legal, accounting, or dispute-resolution
              requirements. Saved obituaries and their PDFs remain in
              your library until you delete them or close the
              account. On account closure, saved obituaries are
              retained for a limited grace period to allow export,
              then deleted or anonymized.
            </p>

            <h2>7. Security</h2>
            <p>
              We use industry-standard safeguards — encrypted
              transport, hashed passwords, narrowly-scoped database
              credentials, regular backups, and server-side controls
              that restrict staff access to production systems. No
              system is perfectly secure; if we learn of a breach
              that materially affects your Firm, we will notify you
              through the contact email on file.
            </p>

            <h2>8. Rights</h2>
            <p>
              Depending on where the Firm&rsquo;s staff reside, they
              may have rights to access, correct, export, or delete
              personal data we hold about them in their capacity as
              account users. Requests can be submitted through the
              in-product Help &amp; contact page. For requests about
              decedent or family information your Firm entered into
              the portal, please route the request through your
              Firm&rsquo;s standard privacy process — we will assist
              your Firm in fulfilling it.
            </p>

            <h2>9. Children</h2>
            <p>
              The portal is not designed for use by children and is
              not directed to them. Do not create a portal account
              for anyone under 18.
            </p>

            <h2>10. International use</h2>
            <p>
              The portal is operated from the United States. If your
              Firm accesses it from another jurisdiction, you
              understand that information you submit may be processed
              in the United States.
            </p>

            <h2>11. Changes</h2>
            <p>
              We may update this Privacy Policy to reflect changes in
              the portal or the law. When we do, we will post the
              revised version with a new &ldquo;Last updated&rdquo;
              date and, for material changes, take reasonable steps
              to notify active Firms.
            </p>

            <h2>12. Contact</h2>
            <p>
              Privacy questions specific to the Funeral Professional
              portal can be raised through the in-product Help &amp;
              contact page from your Firm&rsquo;s dashboard.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FdPrivacy;
